"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.isReadonlyMode = exports.isEditMode = exports.useInlineEditor = exports.INLINE_EDITOR_DELAY_AFTER_SAVING_MILLIS = void 0;
var React = require("react");
var DelayPromise_1 = require("../utilities/DelayPromise");
var TypeScriptHelpers_1 = require("../utilities/TypeScriptHelpers");
var useMergeableState_1 = require("./useMergeableState");
exports.INLINE_EDITOR_DELAY_AFTER_SAVING_MILLIS = 100;
/**
 * The state machine models transitions of the form:
 *
 *     previousState + action = nextState + side effects
 *
 * We can make a simple type-safe implementation using useReducer, but if it gets any more
 * complicated (e.g. with more side effects, more state nodes, or async actions), we should consider
 * switching the implementation to a library such as robot3 or xstate.
 * @param params
 */
function makeStateMachine(params) {
    var canEdit = params.canEdit, canSaveFn = params.canSaveFn, editorStateWhenEnteringEditMode = params.editorStateWhenEnteringEditMode, exitEditModeOnFailedSave = params.exitEditModeOnFailedSave;
    return function inlineEditorStateMachine(prevState, action) {
        switch (prevState.name) {
            case 'readonlyMode':
                switch (action.type) {
                    case 'start-editing':
                        if (canEdit) {
                            return {
                                name: 'editMode_editing',
                                editorState: editorStateWhenEnteringEditMode,
                            };
                        }
                        else {
                            return prevState;
                        }
                    default:
                        return prevState;
                }
            case 'editMode_editing':
                switch (action.type) {
                    case 'set-editor-state-within-edit-mode': {
                        var nextStateOrUpdaterFn = useMergeableState_1.setStateArgForMergeableState(action.nextEditorState);
                        var nextEditorState = typeof nextStateOrUpdaterFn === 'function'
                            ? nextStateOrUpdaterFn(prevState.editorState)
                            : nextStateOrUpdaterFn;
                        return __assign(__assign({}, prevState), { editorState: nextEditorState });
                    }
                    case 'start-save':
                        if (canSaveFn ? canSaveFn(prevState.editorState) : true) {
                            return {
                                name: 'editMode_saving',
                                editorState: prevState.editorState,
                                sideEffects: { onSave: [prevState.editorState] }
                            };
                        }
                        else {
                            return prevState;
                        }
                    case 'cancel-editing':
                        return {
                            name: 'readonlyMode',
                            sideEffects: {
                                onCancel: [prevState.editorState],
                                focusEditButton: true,
                            }
                        };
                    default:
                        return prevState;
                }
            case 'editMode_saving':
                switch (action.type) {
                    case 'abort-save':
                        if (exitEditModeOnFailedSave) {
                            // It might make sense to invoke the onCancel side effect here too, but the previous
                            // implementation didn't, so we're keeping it the same until someone needs it.
                            return {
                                name: 'readonlyMode',
                                sideEffects: { focusEditButton: true }
                            };
                        }
                        else {
                            return {
                                name: 'editMode_editing',
                                editorState: prevState.editorState,
                            };
                        }
                    case 'finish-save':
                        return {
                            name: 'readonlyMode',
                            sideEffects: { focusEditButton: true }
                        };
                    default:
                        return prevState;
                }
            default:
                TypeScriptHelpers_1.assertUnreachable(prevState);
                return prevState;
        }
    };
}
/**
 * The useInlineEditor hook provides state-handling logic for an inline editor scenario
 * (examples: rename a content item or edit its description on its place page, rather than a dedicated modal dialog).
 *
 * This editor allows for two modes:
 *
 * - Readonly mode: Show the data in a readonly mode, with a button that, when clicked, enters edit mode
 * - Edit mode: Provide widgets that allow the user to edit the data, with a save button that, when clicked,
 *   sends the new data to the onSave callback provided by the client.
 *
 * This hook provides the required state, callbacks, and button components to facilitate these two modes,
 * but the client component is expected to provide their actual layout/implementation.
 * See the unit tests in useInlineEditor.test.tsx for a simple usage example.
 *
 * @param params The params passed into the hook. See documentation for InlineEditorParams.
 */
function useInlineEditor(params) {
    var editButtonRef = React.useRef(null);
    var stateMachine = React.useMemo(function () { return makeStateMachine({
        canEdit: params.canEdit,
        editorStateWhenEnteringEditMode: params.editorStateWhenEnteringEditMode,
        exitEditModeOnFailedSave: params.exitEditModeOnFailedSave,
    }); }, [params.canEdit, params.editorStateWhenEnteringEditMode, params.exitEditModeOnFailedSave]);
    var _a = React.useReducer(stateMachine, {
        name: 'readonlyMode',
    }), state = _a[0], dispatch = _a[1];
    // Execute the prescribed side effects from the state machine, if the current state has any
    React.useEffect(function () {
        // This pattern carries a theoretical risk of the props changing, causing the side effects in
        // the deps array to change, thus invalidating the async side effect before it makes its final
        // dispatch. In practice, this is not a problem: props may change, causing the state machine
        // to be rebuilt, but the state itself does not change until another action is dispatched to
        // it, so the side effects don't change unless the state machine actually transitions.
        //
        // This should continue to be okay for us as long as we don't have any side effects that
        // expect to asynchronously dispatch actions to a different state than the one from which they
        // were invoked.
        var isValid = true;
        function applyEffects() {
            var _a, _b, _c;
            return __awaiter(this, void 0, void 0, function () {
                var onSaveParams, e_1;
                return __generator(this, function (_d) {
                    switch (_d.label) {
                        case 0:
                            if (!((_a = state.sideEffects) === null || _a === void 0 ? void 0 : _a.onSave)) return [3 /*break*/, 5];
                            onSaveParams = state.sideEffects.onSave;
                            _d.label = 1;
                        case 1:
                            _d.trys.push([1, 4, , 5]);
                            return [4 /*yield*/, params.onSave.apply(params, onSaveParams)];
                        case 2:
                            _d.sent();
                            return [4 /*yield*/, DelayPromise_1.delayPromise(exports.INLINE_EDITOR_DELAY_AFTER_SAVING_MILLIS)];
                        case 3:
                            _d.sent();
                            if (isValid) {
                                dispatch({ type: 'finish-save' });
                            }
                            return [3 /*break*/, 5];
                        case 4:
                            e_1 = _d.sent();
                            if (isValid) {
                                dispatch({ type: 'abort-save' });
                            }
                            return [3 /*break*/, 5];
                        case 5:
                            if (((_b = state.sideEffects) === null || _b === void 0 ? void 0 : _b.onCancel) && params.onCancel) {
                                params.onCancel.apply(params, state.sideEffects.onCancel);
                            }
                            if (((_c = state.sideEffects) === null || _c === void 0 ? void 0 : _c.focusEditButton) && !params.disableFocusEditButtonOnExitEditMode) {
                                if (editButtonRef.current) {
                                    editButtonRef.current.focus();
                                }
                            }
                            return [2 /*return*/];
                    }
                });
            });
        }
        void applyEffects();
        return function () { isValid = false; };
    }, 
    // This deps array was noncompliant before we enabled the lint rule.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.sideEffects]);
    var setEditorStateWithinEditMode = React.useCallback(function (nextEditorState) { return dispatch({ type: 'set-editor-state-within-edit-mode', nextEditorState: nextEditorState }); }, []);
    if (state.name === 'readonlyMode') {
        return {
            inEditMode: false,
            canEdit: params.canEdit,
            enterEditMode: function () { return dispatch({ type: 'start-editing' }); },
            editButtonRef: editButtonRef,
        };
    }
    else {
        return {
            editorState: state.editorState,
            setEditorStateWithinEditMode: setEditorStateWithinEditMode,
            isActionBusy: state.name === 'editMode_saving',
            inEditMode: true,
            canSaveThisEditorState: params.canSaveFn ? params.canSaveFn(state.editorState) : true,
            startSave: function () { return dispatch({ type: 'start-save' }); },
            cancelEditing: function () { return dispatch({ type: 'cancel-editing' }); },
        };
    }
}
exports.useInlineEditor = useInlineEditor;
function isEditMode(logic) {
    return logic.inEditMode;
}
exports.isEditMode = isEditMode;
function isReadonlyMode(logic) {
    return !logic.inEditMode;
}
exports.isReadonlyMode = isReadonlyMode;
//# sourceMappingURL=useInlineEditor.js.map